import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import toast from 'react-hot-toast';
import {
    IconChevronLeft, IconSend, IconAlertCircle, IconCheck, IconLoader,
} from '@tabler/icons-react';
import {
    usePending360Nominations,
    useSave360Response,
    useSubmit360Response,
} from '../../hooks/usePerformance';

const CATEGORIES = [
    { id: 'leadership',               label: 'Leadership',               description: 'Motivates the team, sets clear direction, and leads by example.' },
    { id: 'problem_solving',          label: 'Problem Solving',          description: 'Analytical thinking and ability to resolve complex issues effectively.' },
    { id: 'responsiveness',           label: 'Responsiveness',           description: 'Accessibility and speed of response to messages and requests.' },
    { id: 'interpersonal_dynamics',   label: 'Interpersonal Dynamics',   description: 'Communication style, inclusiveness, and team relationship management.' },
    { id: 'professionalism',          label: 'Professionalism',          description: 'Ethics, conduct, and maintaining high standards in interactions.' },
    { id: 'subject_matter_expertise', label: 'Subject Matter Expertise', description: 'Technical knowledge and depth of understanding in their field.' },
    { id: 'overall_assessment',       label: 'Overall Assessment',       description: 'Concise summary of performance and areas of impact.' },
];

const RATER_COLORS = {
    self:       { bg: 'bg-primary/10',  text: 'text-primary',  label: 'Self'          },
    supervisor: { bg: 'bg-info/10',     text: 'text-info',     label: 'Supervisor'    },
    supervisee: { bg: 'bg-success/10',  text: 'text-success',  label: 'Direct Report' },
    colleague:  { bg: 'bg-warning/10',  text: 'text-warning',  label: 'Colleague'     },
    external:   { bg: 'bg-purple/10',   text: 'text-purple',   label: 'External'      },
};

export default function Feedback360Respond() {
    const { nominationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { data: pendingList = [], isLoading } = usePending360Nominations();
    const saveResponse   = useSave360Response();
    const submitResponse = useSubmit360Response();

    const nomination = location.state?.nomination
        || pendingList.find(n => String(n.id) === String(nominationId));

    const [comments, setComments]           = useState({});
    const [generalComment, setGeneralComment] = useState('');
    const [isSaving, setIsSaving]           = useState(false);
    const [isSubmitted, setIsSubmitted]     = useState(false);

    useEffect(() => {
        if (nomination?.category_comments) {
            const cats = {};
            nomination.category_comments.forEach(c => { cats[c.category] = c.comment; });
            setComments(cats);
            setGeneralComment(nomination.general_comment || '');
        }
        if (nomination?.status === 'submitted') setIsSubmitted(true);
    }, [nomination]);

    const filledCount = CATEGORIES.filter(cat => comments[cat.id]?.trim()).length;
    const progressPct = Math.round((filledCount / 7) * 100);

    const handleSaveCategory = useCallback(async (categoryKey, value) => {
        if (isSubmitted || !value.trim()) return;
        setIsSaving(true);
        try {
            await saveResponse.mutateAsync({
                nominationId,
                data: { general_comment: generalComment, categories: [{ category: categoryKey, comment: value }] },
            });
        } catch {
        } finally {
            setIsSaving(false);
        }
    }, [nominationId, generalComment, isSubmitted, saveResponse]);

    const handleSubmit = async () => {
        if (filledCount < 7) {
            toast.error('Please complete all 7 categories before submitting.');
            return;
        }
        try {
            await submitResponse.mutateAsync({
                nominationId,
                data: {
                    general_comment: generalComment,
                    categories: CATEGORIES.map(cat => ({ category: cat.id, comment: comments[cat.id] || '' })),
                },
            });
            toast.success('Feedback submitted successfully!');
            navigate('/performance/360-feedback', { state: { tab: 'pending' } });
        } catch (err) {
            const missing = err?.response?.data?.missing_categories;
            if (missing?.length) toast.error(`Missing categories: ${missing.join(', ')}`);
        }
    };

    if (isLoading && !location.state?.nomination) return <PageLoader text="Loading feedback form..." />;

    const subjectName  = nomination?.subject_name || nomination?.subject || '—';
    const cycleName    = nomination?.cycle_name || nomination?.rater_type_display || '';
    const raterColor   = RATER_COLORS[nomination?.rater_type] || RATER_COLORS.colleague;

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Give Feedback" path="Performance" />

            <button onClick={() => navigate('/performance/360-feedback')} className="btn btn-outline-secondary self-start">
                <IconChevronLeft size={15} /> Back
            </button>

            {/* Header card */}
            <div className="bg-bg-dark rounded-lg shadow-light border border-border p-20">
                <div className="flex flex-wrap items-start justify-between gap-15 mb-15">
                    <div>
                        <h2 className="text-[18px] font-bold text-text-primary">
                            {subjectName !== '—' ? subjectName : 'Feedback Request'}
                        </h2>
                        {cycleName && (
                            <p className="text-sm text-text-secondary mt-3">{cycleName}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-8">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full ${raterColor.bg} ${raterColor.text}`}>
                            {nomination?.rater_type_display || raterColor.label}
                        </span>
                        {isSubmitted ? (
                            <span className="inline-flex items-center gap-5 px-8 py-3 rounded-full text-[11px] font-semibold border bg-success/10 text-success border-success/20">
                                <IconCheck size={11} /> Submitted
                            </span>
                        ) : (
                            <span className="text-[10px] text-text-secondary">
                                {isSaving ? 'Saving...' : `${filledCount}/7 complete`}
                            </span>
                        )}
                    </div>
                </div>

                {!isSubmitted && (
                    <div className="flex items-center gap-10">
                        <div className="flex-1 flex gap-[3px]">
                            {CATEGORIES.map((cat, i) => (
                                <div
                                    key={i}
                                    className={`flex-1 h-[5px] rounded-full transition-all duration-300 ${comments[cat.id]?.trim() ? 'bg-success' : 'bg-bg-light'}`}
                                />
                            ))}
                        </div>
                        <span className="text-[10px] font-bold text-text-secondary flex-shrink-0">{filledCount}/7</span>
                    </div>
                )}
            </div>

            {/* Categories */}
            <div className="flex flex-col gap-12">
                {CATEGORIES.map((cat, idx) => (
                    <div key={cat.id} className="bg-bg-dark rounded-lg shadow-light border border-border overflow-hidden">
                        <div className="px-20 py-12 bg-bg-light/30 border-b border-border flex items-center justify-between gap-12">
                            <div className="flex items-center gap-10">
                                <span className={`w-20 h-20 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${comments[cat.id]?.trim() ? 'bg-success text-white' : 'bg-bg-light border border-border text-text-secondary'}`}>
                                    {comments[cat.id]?.trim() ? <IconCheck size={11} /> : idx + 1}
                                </span>
                                <div>
                                    <h3 className="text-[12px] font-bold text-text-primary uppercase tracking-widest">{cat.label}</h3>
                                    <p className="text-[10px] text-text-secondary mt-1">{cat.description}</p>
                                </div>
                            </div>
                        </div>
                        <div className="px-20 py-15 form-group">
                            <textarea
                                rows={3}
                                readOnly={isSubmitted}
                                placeholder={`Your feedback on ${cat.label.toLowerCase()}...`}
                                value={comments[cat.id] || ''}
                                onChange={e => setComments(prev => ({ ...prev, [cat.id]: e.target.value }))}
                                onBlur={e => handleSaveCategory(cat.id, e.target.value)}
                                style={{ resize: 'none', opacity: isSubmitted ? 0.65 : 1 }}
                            />
                        </div>
                    </div>
                ))}

                {/* General comment */}
                <div className="bg-bg-dark rounded-lg shadow-light border border-border overflow-hidden">
                    <div className="px-20 py-12 bg-bg-light/30 border-b border-border">
                        <h3 className="text-[12px] font-bold text-text-primary uppercase tracking-widest">General Comment</h3>
                        <p className="text-[10px] text-text-secondary mt-1">Optional — any additional overall thoughts.</p>
                    </div>
                    <div className="px-20 py-15 form-group">
                        <textarea
                            rows={3}
                            readOnly={isSubmitted}
                            placeholder="Overall great to work with..."
                            value={generalComment}
                            onChange={e => setGeneralComment(e.target.value)}
                            onBlur={e => {
                                if (!isSubmitted && e.target.value.trim()) {
                                    const existingCats = Object.entries(comments)
                                        .filter(([, v]) => v?.trim())
                                        .map(([k, v]) => ({ category: k, comment: v }));
                                    if (existingCats.length > 0) {
                                        saveResponse.mutateAsync({ nominationId, data: { general_comment: e.target.value, categories: existingCats } }).catch(() => {});
                                    }
                                }
                            }}
                            style={{ resize: 'none', opacity: isSubmitted ? 0.65 : 1 }}
                        />
                    </div>
                </div>
            </div>

            {/* Submit */}
            {!isSubmitted && (
                <div className="flex flex-col items-center gap-12 mt-10">
                    <div className="bg-warning/5 border border-warning/20 px-15 py-12 rounded-lg flex items-start gap-10 w-full max-w-lg">
                        <IconAlertCircle className="text-warning flex-shrink-0 mt-1" size={14} />
                        <p className="text-[11px] text-text-secondary leading-relaxed">
                            Once submitted, your feedback cannot be changed. All 7 categories must be filled.
                        </p>
                    </div>
                    <button
                        onClick={handleSubmit}
                        disabled={filledCount < 7 || submitResponse.isPending}
                        className="btn btn-primary"
                    >
                        {submitResponse.isPending ? <IconLoader className="animate-spin" size={14} /> : <IconSend size={14} />}
                        Submit Feedback
                    </button>
                </div>
            )}

            {isSubmitted && (
                <div className="p-20 bg-success/10 rounded-lg border border-success/30 flex items-center justify-center gap-10 text-success">
                    <IconCheck size={16} />
                    <span className="text-sm font-semibold">Feedback submitted successfully</span>
                </div>
            )}
        </div>
    );
}
